import { useEffect, useRef, useState } from "react";

/**
 * Custom-styled Google sign-in.
 * The real Google Identity Services button is rendered transparently on top of
 * our own button: we keep the club styling while using Google's own click
 * handler (the popup flow), which is far more reliable than One Tap prompt().
 */
export default function GoogleAuthButton({ label, onCredential, onError, testid = "google-auth-btn" }) {
  const wrapRef = useRef(null);
  const gisRef = useRef(null);
  const cbRef = useRef({ onCredential, onError });
  const [ready, setReady] = useState(false);

  cbRef.current = { onCredential, onError };

  useEffect(() => {
    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    if (!clientId) return;

    let interval;
    const init = () => {
      if (!window.google?.accounts?.id || !gisRef.current) return false;
      window.google.accounts.id.initialize({
        client_id: clientId,
        callback: (resp) => {
          if (resp?.credential) cbRef.current.onCredential(resp.credential);
          else cbRef.current.onError?.(new Error("No credential returned"));
        },
        ux_mode: "popup",
        auto_select: false,
        cancel_on_tap_outside: true,
      });
      gisRef.current.innerHTML = "";
      window.google.accounts.id.renderButton(gisRef.current, {
        type: "standard",
        theme: "filled_black",
        size: "large",
        text: "continue_with",
        shape: "pill",
        width: Math.min(400, Math.max(200, Math.round(wrapRef.current?.offsetWidth || 320))),
      });
      setReady(true);
      return true;
    };

    if (!init()) {
      interval = setInterval(() => { if (init()) clearInterval(interval); }, 200);
      setTimeout(() => clearInterval(interval), 10000);
    }
    return () => clearInterval(interval);
  }, []);

  const handleClick = () => {
    if (!process.env.REACT_APP_GOOGLE_CLIENT_ID) {
      onError?.(new Error("Missing REACT_APP_GOOGLE_CLIENT_ID"));
      return;
    }
    if (!window.google?.accounts?.id) {
      onError?.(new Error("Google SDK not ready"));
      return;
    }
    // Fallback if the overlay button has not mounted yet.
    window.google.accounts.id.prompt();
  };

  return (
    <div ref={wrapRef} className="relative w-full">
      <button
        type="button"
        onClick={handleClick}
        data-testid={testid}
        className="w-full inline-flex items-center justify-center gap-3 border border-white/15 text-white bg-transparent hover:bg-white/5 hover:border-white/45 rounded-full px-6 py-3.5 text-[12px] tracking-[0.25em] uppercase font-medium transition-colors"
      >
        <svg width="16" height="16" viewBox="0 0 48 48" aria-hidden="true">
          <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.8 1.1 8 3l5.7-5.7C34.5 6.1 29.5 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.2-.1-2.3-.4-3.5z"/>
          <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.6 15.1 18.9 12 24 12c3 0 5.8 1.1 8 3l5.7-5.7C34.5 6.1 29.5 4 24 4 16 4 9.1 8.4 6.3 14.7z"/>
          <path fill="#4CAF50" d="M24 44c5.3 0 10.1-2 13.7-5.3l-6.3-5.2C29.4 34.8 26.9 36 24 36c-5.3 0-9.7-3.3-11.3-8l-6.5 5C9 39.5 16 44 24 44z"/>
          <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.4-2.4 4.4-4.4 5.7l6.3 5.2C40.4 36 44 30.4 44 24c0-1.2-.1-2.3-.4-3.5z"/>
        </svg>
        {label}
      </button>

      <div
        ref={gisRef}
        aria-hidden="true"
        data-testid="google-gis-overlay"
        className={`absolute inset-0 flex items-center justify-center overflow-hidden ${ready ? "opacity-0" : "pointer-events-none opacity-0"}`}
        style={{ colorScheme: "dark" }}
      />
    </div>
  );
}
